// Brain state — the only place live Cipher data becomes a visual state.
//
// `IMPLEMENTATION.md` rule 4: every visible status, node and travelling pulse
// must correspond to real application state. That rule is enforceable only if
// exactly one module does the mapping, so this is it. `BrainStage` renders
// what this returns and decides nothing on its own.
//
// Kept pure — plain functions over plain payloads, no React, no fetch. That is
// what lets the five states be tested without a browser, which matters because
// four of them cannot be produced from Cipher's current data (see
// `deriveDepartmentState` on why `active` is presently unreachable in
// production).
//
// COLOUR DOCTRINE (rule 6, and it is not decorative)
//   cyan    structure          — the org existing and being wired
//   magenta activity/reasoning — something is happening now
//   violet  escalation         — something needs a decision
//   red     critical failure   — reserved, never used for "busy"
// There is no rainbow expansion. A department does not get its own hue.

/** Contract states, in ascending order of demand on the operator's attention. */
export const STATES = Object.freeze({
  HEALTHY: "healthy",
  ACTIVE: "active",
  REASONING: "reasoning",
  ATTENTION: "attention",
  CRITICAL: "critical",
});

/** Precedence when several conditions hold at once. Critical always wins. */
const RANK = {
  [STATES.HEALTHY]: 0,
  [STATES.ACTIVE]: 1,
  [STATES.REASONING]: 2,
  [STATES.ATTENTION]: 3,
  [STATES.CRITICAL]: 4,
};

/** Cipher's canonical department ids, from `backend/services/organization/roster.py`.
 *  The hotspot file binds to exactly these; nothing here invents an id. */
export const DEPARTMENT_LABELS = Object.freeze({
  analytics: "Analytics",
  offer_performance: "Offer Performance",
  creative_intelligence: "Creative Intelligence",
  creative_production: "Creative Production",
  campaign_operations: "Campaign Operations",
  offer_discovery: "Offer Discovery",
  testing: "Testing",
  operator_development: "Operator Development",
  compliance: "Compliance",
  engineering: "Engineering",
  maintenance: "Maintenance",
});

/** A run is "current" if the backend says it is still going. `agent_runs`
 *  stores a status string; anything terminal is not current. */
const TERMINAL_RUN_STATUS = new Set([
  "completed", "failed", "error", "cancelled", "canceled", "timeout", "aborted",
]);

export function isRunCurrent(run) {
  if (!run || typeof run !== "object") return false;
  const status = String(run.status || "").toLowerCase();
  if (!status) return false;
  return !TERMINAL_RUN_STATUS.has(status);
}

/** Group agents by department, counting lifecycle honestly.
 *  Lifecycle values come from `services/organization/contract.py`:
 *  dormant | live | suspended | retired. */
export function summariseAgents(agents) {
  const out = {};
  for (const id of Object.keys(DEPARTMENT_LABELS)) {
    out[id] = { id, label: DEPARTMENT_LABELS[id], total: 0, live: 0, dormant: 0, suspended: 0 };
  }
  for (const a of agents || []) {
    const row = out[a && a.department];
    if (!row) continue; // an unknown department is not silently invented
    row.total += 1;
    const lc = String(a.lifecycle || "").toLowerCase();
    if (lc === "live") row.live += 1;
    else if (lc === "suspended") row.suspended += 1;
    else if (lc === "dormant") row.dormant += 1;
  }
  return out;
}

/**
 * The state for one department.
 *
 * `active` means the department has a run in flight right now. Worth stating
 * plainly: as of this writing Cipher has no live runs, so `active` is
 * unreachable from production data and is exercised only through tests. That
 * is the contract working — "healthy means visually calm" — not a stub.
 */
export function deriveDepartmentState({ suspended = 0, runs = 0, notices = 0, failures = 0 }) {
  if (failures > 0) return STATES.CRITICAL;
  if (suspended > 0 || notices > 0) return STATES.ATTENTION;
  if (runs > 0) return STATES.ACTIVE;
  return STATES.HEALTHY;
}

/** The whole-Brain state is the most demanding of its parts. */
export function rollUp(states) {
  let worst = STATES.HEALTHY;
  for (const s of states) if (RANK[s] > RANK[worst]) worst = s;
  return worst;
}

/**
 * Compose every source into what the stage renders.
 *
 * Sources, all founder-scoped, all already live in this codebase:
 *   agents       GET /api/organization/agents           lifecycle per agent
 *   runs         GET /api/agent-runs                    current runs
 *   notices      GET /api/organization/notifications    unacknowledged founder notices
 *   health       GET /api/organization/health-topology  system failures
 *   reasoning    GET /api/reasoning-pilot/stage-a/status the live cycle, if any
 *
 * Every argument is optional: a surface that cannot read one source must show
 * less, never guess. An absent source contributes nothing rather than zero —
 * the same doctrine the snapshot layer uses for unmeasured economics.
 */
export function composeBrain({ agents, runs, notices, health, reasoning } = {}) {
  const summary = summariseAgents(agents);

  const runsByDept = {};
  for (const r of runs || []) {
    if (!isRunCurrent(r)) continue;
    const d = r.department || (r.agent && r.agent.department);
    if (d && runsByDept[d] !== undefined) runsByDept[d] += 1;
    else if (d) runsByDept[d] = 1;
  }

  const noticesByDept = {};
  for (const n of notices || []) {
    const d = n && n.department;
    if (d) noticesByDept[d] = (noticesByDept[d] || 0) + 1;
  }

  // Only a node the health graph reports as failing counts as critical, and
  // only where it names a department. A degraded integration with no
  // department attribution raises the whole-Brain state, never a hotspot's —
  // colouring an arbitrary region red would be a lie about where the fault is.
  const failuresByDept = {};
  let unattributedFailure = false;
  for (const n of (health && health.nodes) || []) {
    const status = String(n.status || "").toLowerCase();
    if (status !== "failed" && status !== "critical" && status !== "down") continue;
    if (n.department && failuresByDept[n.department] !== undefined) failuresByDept[n.department] += 1;
    else if (n.department) failuresByDept[n.department] = 1;
    else unattributedFailure = true;
  }

  const reasoningDept = reasoning && reasoning.running ? reasoning.department || null : null;

  const departments = Object.keys(DEPARTMENT_LABELS).map((id) => {
    const s = summary[id];
    let state = deriveDepartmentState({
      suspended: s.suspended,
      runs: runsByDept[id] || 0,
      notices: noticesByDept[id] || 0,
      failures: failuresByDept[id] || 0,
    });
    if (id === reasoningDept && RANK[state] < RANK[STATES.REASONING]) state = STATES.REASONING;
    return { ...s, state, runs: runsByDept[id] || 0 };
  });

  const overall = rollUp([
    ...departments.map((d) => d.state),
    unattributedFailure ? STATES.CRITICAL : STATES.HEALTHY,
  ]);

  return {
    departments,
    overall,
    totals: {
      departments: departments.length,
      agents: departments.reduce((n, d) => n + d.total, 0),
      live: departments.reduce((n, d) => n + d.live, 0),
      currentRuns: Object.values(runsByDept).reduce((n, v) => n + v, 0),
    },
    reasoning: reasoningDept ? { department: reasoningDept } : null,
  };
}
